import ExcelJS from "exceljs";

type GenerateMaturityXlsxArgs = {
  assuranceLevel: string;
  fullResponses: Record<string, number>;
  fullNotes: Record<string, string>;
  sessionId: string;
};

type ColIndex = {
  cerinta: number;
  scorD: number;
  scorI: number;
  noteD: number;
  noteI: number;
};

function fold(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

const TEMPLATE_BY_LEVEL: Record<string, string> = {
  [fold("de_baza")]: "/assets/eval-mms-b-v.xlsx",
  [fold("de Bază")]: "/assets/eval-mms-b-v.xlsx",
  [fold("important")]: "/assets/eval-mms-i-v.xlsx",
  [fold("Important")]: "/assets/eval-mms-i-v.xlsx",
  [fold("esential")]: "/assets/eval-mms-e-v.xlsx",
  [fold("Esențial")]: "/assets/eval-mms-e-v.xlsx",
};

const TARGET_SHEETS: ReadonlyArray<string> = [
  fold("GUVERNARE"),
  fold("IDENTIFICARE"),
  fold("PROTECTIE"),
  fold("DETECTARE"),
  fold("RASPUNS"),
  fold("RECUPERARE"),
];

const HEADER_SUBSTRINGS = {
  cerinta: fold("cerinta"),
  scorD: fold("scorul documentatiei"),
  scorI: fold("scorul implementarii"),
  noteD: fold("comentarii si/sau informatii suplimentare"),
  noteI: fold("comentariile evaluatorului"),
} as const;

function pickTemplatePath(level: string): string {
  const path = TEMPLATE_BY_LEVEL[fold(level)];
  if (!path) {
    throw new Error(`Unknown assurance level: ${level}`);
  }
  return path;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function cellText(ws: ExcelJS.Worksheet, r: number, c: number): string {
  const cell = ws.getCell(r, c);
  const v = cell.text ?? cell.value;
  if (v == null) return "";
  if (typeof v === "object") {
    const obj = v as { richText?: { text: string }[]; result?: unknown };
    if (obj.richText) return obj.richText.map((rt) => rt.text).join("");
    if (obj.result != null) return String(obj.result);
    return "";
  }
  return String(v);
}

function findHeaderRow(ws: ExcelJS.Worksheet): number | null {
  const lastScan = Math.min(15, ws.rowCount);
  const lastCol = ws.columnCount;
  for (let r = 1; r <= lastScan; r++) {
    for (let c = 1; c <= lastCol; c++) {
      if (fold(cellText(ws, r, c)) === HEADER_SUBSTRINGS.cerinta) {
        return r;
      }
    }
  }
  return null;
}

function buildColIndex(
  ws: ExcelJS.Worksheet,
  headerRow: number
): ColIndex | null {
  const idx: Partial<ColIndex> = {};
  const lastCol = ws.columnCount;
  for (let c = 1; c <= lastCol; c++) {
    const text = fold(cellText(ws, headerRow, c));
    if (!text) continue;
    if (idx.cerinta === undefined && text.includes(HEADER_SUBSTRINGS.cerinta)) {
      idx.cerinta = c;
    }
    if (idx.scorD === undefined && text.includes(HEADER_SUBSTRINGS.scorD)) {
      idx.scorD = c;
    }
    if (idx.scorI === undefined && text.includes(HEADER_SUBSTRINGS.scorI)) {
      idx.scorI = c;
    }
    if (idx.noteD === undefined && text.includes(HEADER_SUBSTRINGS.noteD)) {
      idx.noteD = c;
    }
    if (idx.noteI === undefined && text.includes(HEADER_SUBSTRINGS.noteI)) {
      idx.noteI = c;
    }
  }
  if (
    idx.cerinta === undefined ||
    idx.scorD === undefined ||
    idx.scorI === undefined ||
    idx.noteD === undefined ||
    idx.noteI === undefined
  ) {
    return null;
  }
  return idx as ColIndex;
}

function extractBaseIds(fullResponses: Record<string, number>): string[] {
  const set = new Set<string>();
  for (const key of Object.keys(fullResponses)) {
    const base = key.replace(/-[DI]$/, "");
    if (base) set.add(base);
  }
  return [...set];
}

function fillSheet(
  ws: ExcelJS.Worksheet,
  headerRow: number,
  cols: ColIndex,
  ids: string[],
  fullResponses: Record<string, number>,
  fullNotes: Record<string, string>
): void {
  const patterns = ids.map((id) => ({
    id,
    re: new RegExp(`\\b${escapeRegex(id)}\\b`),
  }));

  const lastRow = ws.rowCount;
  for (let r = headerRow + 1; r <= lastRow; r++) {
    const text = cellText(ws, r, cols.cerinta);
    if (!text) continue;
    const match = patterns.find((p) => p.re.test(text));
    if (!match) continue;

    const id = match.id;
    const dScore = fullResponses[`${id}-D`];
    const iScore = fullResponses[`${id}-I`];
    const dNote = fullNotes[`${id}-D`];
    const iNote = fullNotes[`${id}-I`];

    if (dScore !== undefined) {
      ws.getCell(r, cols.scorD).value = dScore;
    }
    if (iScore !== undefined) {
      ws.getCell(r, cols.scorI).value = iScore;
    }
    if (dNote && dNote.length > 0) {
      ws.getCell(r, cols.noteD).value = dNote;
    }
    if (iNote && iNote.length > 0) {
      ws.getCell(r, cols.noteI).value = iNote;
    }
  }
}

function triggerDownload(data: ArrayBuffer, filename: string): void {
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

export async function generateMaturityXlsxReport(
  args: GenerateMaturityXlsxArgs
): Promise<void> {
  const { assuranceLevel, fullResponses, fullNotes, sessionId } = args;
  const path = pickTemplatePath(assuranceLevel);

  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch XLSX template: ${response.statusText}`);
  }
  const buf = await response.arrayBuffer();

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);

  const ids = extractBaseIds(fullResponses);

  wb.eachSheet((ws) => {
    if (!TARGET_SHEETS.includes(fold(ws.name))) return;
    const headerRow = findHeaderRow(ws);
    if (headerRow == null) {
      console.error(`[XLSX] Header row not found in sheet "${ws.name}"`);
      return;
    }
    const cols = buildColIndex(ws, headerRow);
    if (!cols) {
      console.error(`[XLSX] Required columns missing in sheet "${ws.name}"`);
      return;
    }
    fillSheet(ws, headerRow, cols, ids, fullResponses, fullNotes);
  });

  const out = await wb.xlsx.writeBuffer();
  triggerDownload(out as ArrayBuffer, `evaluare-maturitate-${sessionId}.xlsx`);
}
